import { useEffect, useMemo, useState } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import { Box, CircularProgress, IconButton, InputAdornment, Tooltip, Typography } from '@mui/material';
import { GridActionsCellItem, GridRowModes, GridToolbarContainer, useGridApiRef } from '@mui/x-data-grid-premium';
import { v4 as uuidv4 } from 'uuid';

import {
  AddButtonUi,
  AutoCompleteSelectUI,
  ButtonUI,
  ConfirmDialogUI,
  DataGridIdentifiers,
  DatagridPremiumUi,
  DialogUI,
  InputUI,
} from '../Interface';
import printTranslationService from '../../service/printTranslation.service';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { printTranslationConfig } from '../../config/printTranslation';

const EditToolbar = ({ disabled, setRows, setRowModesModel }) => {
  const handleAdd = () => {
    const id = uuidv4();
    setRows((currentRows) => [
      ...currentRows,
      {
        id,
        persistedId: null,
        langCode: null,
        languageName: '',
        translatedValue: '',
        originalValue: '',
        isNew: true,
      },
    ]);
    setRowModesModel((currentModel) => ({
      ...currentModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'langCode' },
    }));
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleAdd} />
    </GridToolbarContainer>
  );
};

const PrintTranslationFieldButton = ({ resourceCode, recordKey, fieldName, isEditMode = false }) => {
  const apiRef = useGridApiRef();
  const [open, setOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [savedRows, setSavedRows] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const resourceConfig = printTranslationConfig[resourceCode];
  const available = Boolean(
    resourceConfig?.fields?.includes(fieldName) && recordKey !== null && recordKey !== undefined
  );

  const field = useMemo(() => data?.fields?.find((item) => item.fieldName === fieldName), [data, fieldName]);
  const isDirty = useMemo(() => {
    if (deletedIds.length) return true;

    const comparableRows = (items) =>
      items
        .map(({ persistedId, langCode, translatedValue }) => ({ persistedId, langCode, translatedValue }))
        .sort((left, right) =>
          String(left.persistedId ?? left.langCode).localeCompare(String(right.persistedId ?? right.langCode))
        );

    return JSON.stringify(comparableRows(rows)) !== JSON.stringify(comparableRows(savedRows));
  }, [deletedIds, rows, savedRows]);
  const getLanguageOptions = (rowId) => {
    const selectedLanguageCodes = new Set(
      rows.filter((row) => row.id !== rowId && row.langCode).map((row) => row.langCode)
    );
    return (data?.languages ?? [])
      .filter((language) => !selectedLanguageCodes.has(language.code))
      .map((language) => ({ value: language.code, label: `${language.name} (${language.code})` }));
  };

  const handleDeleteClick = (id) => () => {
    const row = rows.find((item) => item.id === id);
    if (row?.persistedId) setDeletedIds((currentIds) => [...currentIds, row.persistedId]);
    setRows((currentRows) => currentRows.filter((item) => item.id !== id));
  };

  const columns = [
    {
      field: 'langCode',
      headerName: 'Nyelv',
      minWidth: 200,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (row.langCode ? `${row.languageName} (${row.langCode})` : ''),
      renderEditCell: (params) => {
        const selectedLanguage = data?.languages?.find((language) => language.code === params.row.langCode);
        return (
          <AutoCompleteSelectUI
            id={`translation-language-${params.id}`}
            fullWidth
            variant="standard"
            dataset={getLanguageOptions(params.id)}
            selectedValue={params.row.langCode}
            selectedLabelValue={
              selectedLanguage ? `${selectedLanguage.name} (${selectedLanguage.code})` : params.row.languageName
            }
            disabled={Boolean(params.row.persistedId)}
            onChange={(_event, newValue) => {
              const language = data?.languages?.find((item) => item.code === newValue?.value);
              setRows((currentRows) =>
                currentRows.map((row) =>
                  row.id === params.id
                    ? { ...row, langCode: newValue?.value ?? null, languageName: language?.name ?? '' }
                    : row
                )
              );
              params.api.setEditCellValue({ id: params.id, field: params.field, value: newValue?.value ?? null });
            }}
          />
        );
      },
    },
    {
      field: 'translatedValue',
      headerName: 'Fordított érték',
      minWidth: 300,
      flex: 2,
      sortable: false,
      editable: true,
      renderEditCell: (params) => (
        <InputUI
          fullWidth
          variant="standard"
          value={params.row.translatedValue}
          onChange={(event) => {
            setRows((currentRows) =>
              currentRows.map((item) =>
                item.id === params.id ? { ...item, translatedValue: event.target.value } : item
              )
            );
            params.api.setEditCellValue({ id: params.id, field: params.field, value: event.target.value });
          }}
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 50,
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Törlés"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleDeleteClick(id)();
            }}
            color="inherit"
          />,
        ];
      },
    },
  ];

  useEffect(() => {
    if (!open) return;

    setLoading(true);
    printTranslationService
      .getData(resourceCode, recordKey)
      .then((response) => {
        setData(response);
        setDeletedIds([]);
        setRowModesModel({});
        const languagesByCode = new Map((response.languages ?? []).map((language) => [language.code, language]));
        const loadedRows = (response.translations ?? [])
          .filter((translation) => translation.fieldName === fieldName)
          .map((translation) => {
            const language = languagesByCode.get(translation.langCode);
            return {
              id: translation.id,
              persistedId: translation.id,
              langCode: translation.langCode,
              originalLangCode: translation.langCode,
              languageName: language?.name ?? translation.langCode,
              translatedValue: translation.translatedValue,
              originalValue: translation.translatedValue,
              isNew: false,
            };
          });
        setRows(loadedRows);
        setSavedRows(loadedRows);
      })
      .catch(() => setOpen(false))
      .finally(() => setLoading(false));
  }, [fieldName, open, recordKey, resourceCode]);

  const handleSave = async () => {
    if (rows.some((row) => !row.langCode || !row.translatedValue?.trim())) {
      addNotification({
        content: 'Minden sorban kötelező nyelvet és fordított értéket megadni!',
        type: NotificationType.ERROR,
      });
      return;
    }
    if (new Set(rows.map((row) => row.langCode)).size !== rows.length) {
      addNotification({ content: 'Egy nyelv csak egyszer választható!', type: NotificationType.ERROR });
      return;
    }

    const changedRows = rows.filter((row) => !row.persistedId || row.translatedValue !== row.originalValue);
    if (!changedRows.length && !deletedIds.length) {
      return;
    }

    setSaving(true);
    try {
      await Promise.all(deletedIds.map((id) => printTranslationService.delete(id)));
      await Promise.all(
        changedRows.map((row) => {
          if (row.persistedId && !row.translatedValue.trim()) {
            return printTranslationService.delete(row.persistedId);
          }
          if (row.persistedId) {
            return printTranslationService.update({ id: row.persistedId, translatedValue: row.translatedValue });
          }
          if (!row.translatedValue.trim()) return Promise.resolve();
          return printTranslationService.create({
            resourceCode,
            recordKey: String(recordKey),
            fieldName,
            langCode: row.langCode,
            translatedValue: row.translatedValue,
          });
        })
      );

      const response = await printTranslationService.getData(resourceCode, recordKey);
      const languagesByCode = new Map((response.languages ?? []).map((language) => [language.code, language]));
      const loadedRows = (response.translations ?? [])
        .filter((translation) => translation.fieldName === fieldName)
        .map((translation) => {
          const language = languagesByCode.get(translation.langCode);
          return {
            id: translation.id,
            persistedId: translation.id,
            langCode: translation.langCode,
            originalLangCode: translation.langCode,
            languageName: language?.name ?? translation.langCode,
            translatedValue: translation.translatedValue,
            originalValue: translation.translatedValue,
            isNew: false,
          };
        });
      setData(response);
      setRows(loadedRows);
      setSavedRows(loadedRows);
      setDeletedIds([]);
      setRowModesModel({});
      addNotification({ content: 'A fordítások mentése sikeres!', type: NotificationType.SUCCESS });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelChanges = () => {
    setRowModesModel(
      Object.fromEntries(rows.map((row) => [row.id, { mode: GridRowModes.View, ignoreModifications: true }]))
    );
    setRows(savedRows.map((row) => ({ ...row })));
    setDeletedIds([]);
  };

  const handleClose = () => {
    if (isDirty) {
      setCloseConfirmOpen(true);
      return;
    }
    setOpen(false);
  };

  const handleDiscardAndClose = () => {
    setCloseConfirmOpen(false);
    setOpen(false);
  };

  return (
    <>
      <InputAdornment position="end">
        <Tooltip
          title={
            !isEditMode
              ? 'A fordítások mentett rekord szerkesztésekor érhetők el'
              : available
                ? 'Fordítások'
                : 'A fordítás mentett rekordnál érhető el'
          }
        >
          <span>
            <IconButton
              size="small"
              color="primary"
              disabled={!isEditMode || !available}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => setOpen(true)}
            >
              <LanguageIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </InputAdornment>

      <DialogUI
        open={open}
        onClose={handleClose}
        headerContent={`${field?.label ?? 'Mező'} fordításai`}
        maxWidth="md"
        fullWidth
        closeBtn
        actionsContent={
          <div className="flex gap-2">
            <ButtonUI
              text="Mentés"
              className="bg-success"
              onClick={handleSave}
              disabled={loading || saving || !isDirty}
            />
            <ButtonUI
              text="Mégsem"
              className="bg-primary"
              onClick={handleCancelChanges}
              disabled={loading || saving || !isDirty}
            />
          </div>
        }
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ my: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Eredeti érték
              </Typography>
              <Typography>{field?.sourceValue || '—'}</Typography>
            </Box>
            <Box sx={{ height: 360, width: '100%' }}>
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.PrintTranslation}
                paginationAndfilterMode="client"
                disableSave
                headerFilters={false}
                height={360}
                rows={rows}
                columns={columns}
                rowHeight={56}
                hideFooter
                editMode="row"
                rowModesModel={rowModesModel}
                onRowModesModelChange={setRowModesModel}
                slots={{ toolbar: EditToolbar }}
                slotProps={{
                  toolbar: {
                    disabled: rows.filter((row) => row.langCode).length >= (data?.languages ?? []).length,
                    setRows,
                    setRowModesModel,
                  },
                }}
                onCellClick={(params) => {
                  if (params.field === 'actions') return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
              />
            </Box>
          </>
        )}
      </DialogUI>
      <ConfirmDialogUI
        show={closeConfirmOpen}
        title="Nem mentett módosítások"
        message="El nem mentett módosításai vannak. Biztosan bezárja az ablakot?"
        confirmButtonText="Bezárás mentés nélkül"
        handleChangeElement={handleDiscardAndClose}
        onHide={() => setCloseConfirmOpen(false)}
      />
    </>
  );
};

export default PrintTranslationFieldButton;
